import { Link } from 'react-router';
import { Fragment } from 'react';
import { dealJumpHref } from './leadDealLinks';
import { leadHref, orderHref } from '../profiles/profileLinks';
import type { DealJourney, LeadJourney } from './linkedinJourney';
import './linkedinJourney.css';

export function linkedinDate(value: string | null) {
  if (value === null) return 'Date not recorded';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Manila',
  }).format(new Date(value.length === 10 ? `${value}T00:00:00+08:00` : value));
}
export function JourneyCounts({ deals }: { deals: readonly DealJourney[] }) {
  const cg = deals.filter((d) => d.pipeline === 'cg_deals');
  const orders = cg.reduce((n, d) => n + (d.cg_order_count ?? 0), 0);
  return (
    <p className="tracker-field-note">
      <span>Deals {deals.length}</span> ·{' '}
      <span>
        CG {cg.length} · Partnerships {deals.filter((d) => d.pipeline === 'partnerships').length}
      </span>{' '}
      · <span>CG orders {orders}</span>
    </p>
  );
}
export function JourneyStrip({
  row,
  deals,
}: {
  row: LeadJourney | DealJourney | null | undefined;
  deals?: readonly DealJourney[] | undefined;
}) {
  if (!row?.prospect_id) return null;
  const isDeal = 'deal_id' in row;
  const steps: {
    label: string;
    date: string | null;
    href?: string;
    state: 'done' | 'current' | 'future';
    more?: number;
  }[] = [
    {
      label: 'Prospect',
      date: row.prospect_created_at,
      href: `/tracker?tab=linkedin&prospect=${row.prospect_id}`,
      state: 'done',
    },
    {
      label: 'Lead',
      date: row.promoted_at,
      ...(row.lead_id ? { href: leadHref(row.lead_id) } : {}),
      state: isDeal ? 'done' : 'current',
    },
  ];
  if (isDeal) {
    const hasOrder = row.pipeline === 'cg_deals' && (row.cg_order_count ?? 0) > 0;
    const end =
      row.outcome === 'lost'
        ? 'Lost'
        : hasOrder
          ? 'CG order'
          : row.pipeline === 'partnerships' && row.outcome === 'won'
            ? 'Won (contract signed)'
            : null;
    steps.push({
      label: row.pipeline === 'partnerships' ? 'Deal (Partnerships)' : 'Deal (CG)',
      date: row.deal_created_at,
      state: end ? 'done' : 'current',
    });
    if (end)
      steps.push({
        label: end,
        date: end === 'CG order' ? null : row.closed_on,
        state: 'current',
        ...(end === 'CG order' && row.first_cg_order_id
          ? {
              href: orderHref(row.first_cg_order_id),
              more: Math.max(0, (row.cg_order_count ?? 1) - 1),
            }
          : {}),
      });
  } else if (deals?.length === 0) {
    steps.push({ label: 'Deal', date: null, state: 'future' });
  } else if (deals) {
    for (const d of deals)
      steps.push({
        label: d.pipeline === 'partnerships' ? 'Deal (Partnerships)' : 'Deal (CG)',
        date: d.deal_created_at,
        state: 'done',
        ...(d.deal_id && (d.pipeline === 'cg_deals' || d.pipeline === 'partnerships')
          ? { href: dealJumpHref({ id: d.deal_id, pipeline: d.pipeline }) }
          : {}),
      });
  }
  return (
    <nav className="linkedin-journey" aria-label="Customer journey">
      {steps.map((s, i) => (
        <Fragment key={`${s.label}-${i}`}>
          {i > 0 && <span aria-hidden="true">›</span>}
          <div
            className={`linkedin-journey-step linkedin-journey-step--${s.state}`}
            aria-current={s.state === 'current' ? 'step' : undefined}
          >
            {s.href ? <Link to={s.href}>{s.label}</Link> : <strong>{s.label}</strong>}
            <small>
              {s.state === 'future'
                ? 'Not yet'
                : s.label === 'CG order'
                  ? 'Linked order'
                  : linkedinDate(s.date)}
            </small>
            {(s.more ?? 0) > 0 && <small>+{s.more} more</small>}
          </div>
        </Fragment>
      ))}
    </nav>
  );
}
